import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Power } from "lucide-react";
import { apiGet, protectedPost } from "@/lib/api";

export default function TerminalPage() {
  const [enabled, setEnabled] = useState(false);
  const [lines, setLines] = useState([
    { id: "welcome", type: "sys", text: "JARVIS diagnostics — read-only health, disk and process checks. Type help for allowed commands. No shell or arbitrary programs." },
  ]);
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    apiGet("/terminal/config").then((c) => setEnabled(c.enabled)).catch(() => {});
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  const toggle = async () => {
    try {
      const res = await protectedPost("/terminal/config", { enabled: !enabled });
      setEnabled(res.enabled);
      toast[res.enabled ? "success" : "message"](`Terminal ${res.enabled ? "enabled" : "locked"}, Sir.`);
    } catch {
      toast.error("Unlock required to change terminal state.");
    }
  };

  const run = async () => {
    const c = cmd.trim();
    if (!c || busy) return;
    setLines((p) => [...p, { id: crypto.randomUUID(), type: "cmd", text: c }]);
    setCmd("");
    setBusy(true);
    try {
      const res = await protectedPost("/terminal/exec", { command: c });
      setLines((p) => [...p, { id: crypto.randomUUID(), type: res.exit_code === 0 ? "out" : "err", text: res.output || "(no output)" }]);
    } catch (e) {
      setLines((p) => [...p, { id: crypto.randomUUID(), type: "err", text: e.message || "Execution blocked." }]);
    }
    setBusy(false);
    inputRef.current?.focus();
  };

  return (
    <div className="h-full p-4 lg:p-5">
      <div className="jv-panel h-full flex flex-col" onClick={() => enabled && inputRef.current?.focus()} data-testid="terminal-panel">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-jarvis-cyan/10">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-jarvis-red/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-jarvis-amber/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-jarvis-green/70" />
            <span className="ml-2 font-mono text-[11px] uppercase tracking-[0.25em] text-jarvis-cyanBright/90">jarvis@core:diagnostics</span>
          </div>
          <button
            onClick={toggle}
            data-testid="terminal-toggle"
            className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md border transition-all ${enabled ? "border-jarvis-green/50 text-jarvis-green bg-jarvis-green/10" : "border-jarvis-red/40 text-jarvis-red bg-jarvis-red/10"}`}
          >
            <Power className="w-3.5 h-3.5" /> {enabled ? "ON" : "OFF"}
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 font-code text-[12.5px] leading-relaxed relative">
          {lines.map((l) => {
            if (l.type === "cmd")
              return (
                <div key={l.id} className="text-slate-100">
                  <span className="text-jarvis-green">jarvis@core</span>
                  <span className="text-slate-500">:</span>
                  <span className="text-jarvis-cyan">diagnostics</span>
                  <span className="text-slate-500">$ </span>
                  {l.text}
                </div>
              );
            const color = l.type === "err" ? "text-jarvis-red/90" : l.type === "sys" ? "text-slate-500" : "text-slate-300";
            return <pre key={l.id} className={`whitespace-pre-wrap ${color}`}>{l.text}</pre>;
          })}
          {busy && <div className="text-jarvis-amber">executing…</div>}
          {enabled && (
            <div className="flex items-center text-slate-100">
              <span className="text-jarvis-green">jarvis@core</span>
              <span className="text-slate-500">:</span>
              <span className="text-jarvis-cyan">diagnostics</span>
              <span className="text-slate-500">$&nbsp;</span>
              <input
                ref={inputRef}
                data-testid="terminal-input-field"
                value={cmd}
                onChange={(e) => setCmd(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                autoFocus
                spellCheck={false}
                className="flex-1 bg-transparent outline-none border-none text-slate-100 font-code"
              />
            </div>
          )}
          <div ref={endRef} />

          {!enabled && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-[#050914]/80 backdrop-blur-sm"
              data-testid="terminal-locked-overlay"
            >
              <Lock className="w-8 h-8 text-jarvis-red mb-3" />
              <div className="font-mono text-sm uppercase tracking-[0.2em] text-slate-300">Terminal Locked</div>
              <p className="font-mono text-[11px] text-slate-500 mt-2 max-w-xs text-center">
                Only approved read-only diagnostics are available. Arbitrary programs, arguments and shell syntax are blocked even when enabled.
              </p>
              <button
                onClick={toggle}
                data-testid="terminal-enable-btn"
                className="mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded-md border border-jarvis-red/40 text-jarvis-red hover:bg-jarvis-red/15 transition-all"
              >
                <Power className="w-4 h-4" /> Enable Terminal
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
